import { createFileRoute } from "@tanstack/react-router";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SubscribeButton } from "@/components/payments/subscribe-button";
import { ManageSubscriptionButton } from "@/components/payments/manage-subscription-button";
import { SubscriptionStatusCard } from "@/components/subscriptions/subscription-status-card";
import { SubscriptionStatusCardSkeleton } from "@/components/subscriptions/subscription-status-card-skeleton";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Zap, Shield, Clock, Settings } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useConvexAuth } from "convex/react";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/subscriptions")({
  component: SubscriptionsPage,
});

function SubscriptionsPage() {
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  const user = useQuery(api.system.users.queries.getCurrentUser);

  const subscriptionStatus = useQuery(
    api.companies.subscriptions.queries.getSubscriptionStatus,
    user?.internalControl?.companyId
      ? { companyId: user.internalControl.companyId }
      : "skip",
  );

  const billing = useQuery(api.integrations.dodo_payments.billing.getBillingSummary);

  // El botón de la tarjeta de estado lleva al plan de abajo.
  const handleUpgrade = () => {
    document.getElementById("plan-a-pagar")?.scrollIntoView({ behavior: "smooth" });
  };

  if (authLoading || !user) {
    return (
<RouteGuardPermissions
      permissions={[{ module: "subscription_plans", actions: ["read"] }]}>

      <div className="container mx-auto py-8">
        <div className="mb-10 text-center">
          <Skeleton className="h-10 w-64 mx-auto" />
          <Skeleton className="h-6 w-96 mx-auto mt-4" />
        </div>
        <div className="max-w-2xl mx-auto">
          <Skeleton className="h-100 w-full rounded-lg" />
        </div>
      </div>

    </RouteGuardPermissions>
    );
  }

  const companyId = user?.internalControl?.companyId;
  if (!companyId) {
    return (
      <div className="container mx-auto py-8">
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-bold tracking-tight">
            Planes de Suscripción
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            No se pudo identificar tu empresa
          </p>
        </div>
        <div className="max-w-2xl mx-auto p-6 border border-dashed border-border rounded-lg text-center">
          <p className="text-muted-foreground">
            Tu usuario no está asociado a una empresa. Contacta al administrador
            del sistema.
          </p>
        </div>
      </div>
    );
  }

  if (!subscriptionStatus) {
    return (
      <div className="container mx-auto py-8">
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-bold tracking-tight">
            Planes de Suscripción
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            No se encontró información de suscripción para tu empresa
          </p>
        </div>
        <div className="max-w-2xl mx-auto p-6 border border-dashed border-border rounded-lg text-center">
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Tu empresa no tiene una suscripción configurada en el sistema.
            </p>
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Contacta al administrador del sistema para configurar tu
                suscripción o ejecutar la migración.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-10 text-center">
        <h1 className="text-4xl font-bold tracking-tight">
          Planes de Suscripción
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Elige el plan que mejor se adapte a las necesidades de tu empresa
        </p>
      </div>

      <div className="max-w-2xl mx-auto mb-10">
        {subscriptionStatus === undefined ? (
          <SubscriptionStatusCardSkeleton />
        ) : subscriptionStatus !== null ? (
          <SubscriptionStatusCard
            status={subscriptionStatus}
            onUpgrade={handleUpgrade}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-center">
              <Badge
                variant="outline"
                className="bg-gray-50 text-gray-700 border-gray-200"
              >
                <Settings className="w-3 h-3 mr-1" />
                No disponible
              </Badge>
            </CardContent>
          </Card>
        )}
      </div>

      <div id="plan-a-pagar" className="max-w-md mx-auto scroll-mt-6">
        <Card className="relative border-primary shadow-lg">
          {billing === undefined ? (
            <CardContent className="p-6">
              <Skeleton className="h-48 w-full" />
            </CardContent>
          ) : billing?.offer ? (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">{billing.offer.name}</CardTitle>
                {billing.offer.description && (
                  <CardDescription>{billing.offer.description}</CardDescription>
                )}
                <div className="mt-4">
                  <span className="text-4xl font-bold">
                    {formatPrice(billing.offer.price, billing.offer.currency)}
                  </span>
                  <span className="text-muted-foreground">
                    {billing.offer.billingPeriod === "yearly" ? "/año" : "/mes"}
                  </span>
                </div>
              </CardHeader>

              {billing.offer.features.length > 0 && (
                <CardContent>
                  <ul className="space-y-3">
                    {billing.offer.features.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              )}

              <CardFooter>
                <SubscribeButton
                  planName={billing.offer.name}
                  className="w-full"
                  variant="default"
                  size="lg"
                >
                  Suscribirse ahora
                </SubscribeButton>
              </CardFooter>
            </>
          ) : (
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              Todavía no hay un plan disponible para comprar en línea. Escríbenos a{" "}
              <a href="mailto:hola@heskala.com" className="text-primary hover:underline">
                hola@heskala.com
              </a>{" "}
              y te ayudamos a activarlo.
            </CardContent>
          )}
        </Card>
      </div>

      <div className="mt-8 max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Gestiona tu suscripción actual
            </CardTitle>
            <CardDescription>
              Accede al portal de cliente para actualizar tu plan, cambiar
              método de pago o cancelar tu suscripción
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <Zap className="h-10 w-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold">Actualizar Plan</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Cambia a un plan superior o inferior según tus necesidades
                </p>
              </div>
              <div className="text-center p-4">
                <Shield className="h-10 w-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold">Métodos de Pago</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Agrega, actualiza o elimina tus métodos de pago
                </p>
              </div>
              <div className="text-center p-4">
                <Clock className="h-10 w-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold">Historial de Pagos</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Revisa tus facturas y recibos de pago anteriores
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center">
            <ManageSubscriptionButton
              variant="default"
              size="lg"
              className="px-8"
              showIcon={true}
            >
              Acceder al Portal de Cliente
            </ManageSubscriptionButton>
          </CardFooter>
        </Card>
      </div>

      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p>
          ¿Necesitas un plan personalizado?{" "}
          <a
            href="mailto:hola@heskala.com"
            className="text-primary hover:underline"
          >
            Contáctanos
          </a>
        </p>
        <p className="mt-2">
          El cobro lo procesa Dodo Payments, en dólares. Cancela en cualquier
          momento desde el portal del cliente.
        </p>
      </div>
    </div>
  );
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat("es-HN", { style: "currency", currency }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}
